<script setup>
import { ref } from 'vue';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const toast = useToast();

const linkedinRef = ref(null);
const facebookRef = ref(null);
const twitterRef = ref(null);

const props = defineProps({
  title: String,
  slug: String,
  id: String,
  metaDescription: String,
  tags: {
    type: Array,
    default: () => [], // Default to empty array to prevent join error
  },
  align: {
    type: String,
    default: 'horizontal', // Default to horizontal alignment
    validator: (value) => ['horizontal', 'vertical'].includes(value), // Validate prop values
  },
});

function linkedinWorkaround() {
  if (linkedinRef.value === null) return;

  clearInterval(linkedinRef.value.popupInterval);
  linkedinRef.value.popupWindow = undefined;

  const popupWindow = linkedinRef.value?.popupWindow;

  if (!popupWindow) return;

  try {
    const sameOrigin = popupWindow.location.origin === window.location.origin;

    if (sameOrigin) {
      clearInterval(linkedinRef.value.popupInterval);
      linkedinRef.value.popupWindow = undefined;
    } else {
      console.warn('Popup is cross-origin. Skipping Vue-specific checks.');
    }
  } catch (e) {
    console.warn('Cross-origin popup access blocked:', e.message);
  }
}

function facebookWorkaround() {
  if (facebookRef.value === null) return;

  clearInterval(facebookRef.value.popupInterval);
  facebookRef.value.popupWindow = undefined;
}

function twitterWorkaround() {
  if (twitterRef.value === null) return;

  clearInterval(twitterRef.value.popupInterval);
  twitterRef.value.popupWindow = undefined;
}

const url = window.location.href;

function copyUrlToClipboard() {
  navigator.clipboard
    .writeText(url)
    .then(() => {
      toast.open({
        message: 'Copied to clipboard',
        type: 'info',
        duration: 1000,
      });
    })
    .catch((err) => {
      console.error('Failed to copy:', err);
    });
}
</script>

<template>
  <div class="d-flex justify-space-between align-center mt-4">
    <p class="share-text">Share:</p>
    <div class="social-icons" :class="{ vertical: props.align === 'vertical' }">
      <ShareNetwork
        network="linkedin"
        :url="url"
        :title="title"
        ref="linkedinRef"
        @open="linkedinWorkaround"
      >
        <div class="social-icon linkedin">
          <svg viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M11.0005 10.464H15.3335V12.6223C15.9577 11.381 17.5584 10.2657 19.9629 10.2657C24.5724 10.2657 25.6667 12.7367 25.6667 17.2703V25.6668H21V18.3028C21 15.721 20.3759 14.265 18.7869 14.265C16.583 14.265 15.6672 15.8342 15.6672 18.3017V25.6668H11.0005V10.464ZM2.99837 25.4685H7.66504V10.2657H2.99837V25.4685ZM8.33354 5.3085C8.33371 5.69965 8.25614 6.08694 8.10533 6.44785C7.95451 6.80876 7.73347 7.1361 7.45504 7.41083C6.89084 7.97156 6.12715 8.28543 5.33171 8.2835C4.53766 8.28296 3.77574 7.9699 3.21071 7.412C2.93329 7.13633 2.71299 6.80862 2.56244 6.44767C2.41189 6.08671 2.33405 5.69959 2.33337 5.3085C2.33337 4.51866 2.64837 3.76266 3.21187 3.205C3.77641 2.64635 4.53865 2.33315 5.33287 2.3335C6.12854 2.3335 6.89154 2.64733 7.45504 3.205C8.01737 3.76266 8.33354 4.51866 8.33354 5.3085Z"
              fill="currentColor"
            />
          </svg>
        </div>
      </ShareNetwork>
      <ShareNetwork
        network="facebook"
        :url="url"
        :title="title"
        :description="metaDescription"
        :hashtags="tags.join(',')"
        ref="facebookRef"
        @open="facebookWorkaround"
      >
        <div class="social-icon facebook">
          <svg viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M16.3333 15.7502H19.25L20.4166 11.0835H16.3333V8.75016C16.3333 7.5485 16.3333 6.41683 18.6666 6.41683H20.4166V2.49683C20.0363 2.44666 18.6001 2.3335 17.0835 2.3335C13.916 2.3335 11.6666 4.26666 11.6666 7.81683V11.0835H8.16663V15.7502H11.6666V25.6668H16.3333V15.7502Z"
              fill="currentColor"
            />
          </svg>
        </div>
      </ShareNetwork>
      <ShareNetwork
        network="twitter"
        :url="url"
        :title="title"
        :hashtags="tags.join(',')"
        ref="twitterRef"
        @open="twitterWorkaround"
      >
        <div class="social-icon twitter">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
            <path
              d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z"
              fill="currentColor"
            />
          </svg>
        </div>
      </ShareNetwork>
      <div class="social-icon copy" @click="copyUrlToClipboard">
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.375 3.7998H4.1123V5.2002H3.17383V5.40039L3.1748 16.2002V16.4004H11.0752V15.5H12.5488L12.5449 16.1943C12.5343 16.5843 12.4355 16.9242 12.2148 17.1973L12.1133 17.3096C11.7955 17.625 11.3675 17.7997 10.9072 17.7998H3.31348C2.42234 17.7998 1.7003 17.081 1.7002 16.2002V5.40039C1.7002 4.52576 2.44362 3.7998 3.375 3.7998ZM10.7139 0.200195H14.6865C15.5777 0.200195 16.2997 0.918991 16.2998 1.7998V12.5996C16.2998 13.4806 15.5777 14.2002 14.6875 14.2002H7.06348C6.17228 14.2002 5.4502 13.4805 5.4502 12.5996V5.24609C5.4502 4.66326 5.65629 4.11061 6.03809 3.66504L6.21289 3.48047L8.86426 0.935547C9.35671 0.461965 10.0133 0.200195 10.7139 0.200195ZM11.8115 1.59375L11.6123 1.59473V4.5C11.6123 5.37463 10.8689 6.09961 9.9375 6.09961H6.9248V12.7998H14.8877L14.8867 12.5986L14.8252 1.79883C14.8251 1.76748 14.8189 1.73836 14.8096 1.71289C14.8049 1.70036 14.7996 1.68862 14.7939 1.67871C14.7912 1.67388 14.788 1.6692 14.7852 1.66504L14.7793 1.65625L14.7773 1.6543V1.65332L14.7168 1.57617H14.6182L11.8115 1.59375ZM9.79883 2.02832L7.375 4.35547L7.01562 4.7002H10.1377V1.70312L9.79883 2.02832Z"
            fill="currentColor"
          />
        </svg>
      </div>
    </div>
  </div>
</template>

<style scoped>
.share-text {
  font-weight: 500;
  font-size: 20px;
  line-height: 24px;
  color: #292929;
}
.social-icons {
  display: flex;
  gap: 16px; /* Matches the original ga-4 (gap: 16px) */
}
.social-icons.vertical {
  flex-direction: column;
}
.social-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: 6px;
  border: 2px solid #0000004d;
  width: fit-content;
  border-radius: 50%;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}
svg {
  color: #0000004d;
  height: 18px;
  width: 18px;
}
.linkedin:hover {
  background-image: linear-gradient(to right, #007bbb, #0044e9);
  border-color: #0e76a8;
}
.linkedin:hover svg {
  color: white;
}
.facebook:hover {
  background-image: linear-gradient(to right, #1677f1, #2b189c);
  border-color: #1677f1;
}
.facebook:hover svg {
  color: white;
}
.twitter:hover {
  background-image: linear-gradient(to right, #23004a, #000000);
  border-color: #23004a;
}
.twitter:hover svg {
  color: white;
}
.copy:hover {
  background-color: #00aeff;
  border-color: #00aeff;
}
.copy:hover svg {
  color: white;
}
</style>
